import {Button} from 'antd';
import React from 'react';
import {createPortal} from 'react-dom';
import {
  Bar,
  ComposedChart,
  Line,
  Rectangle,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis
} from 'recharts';

import {ReactComponent as AIInsightSparkle} from '../../../../assets/icons/AIInsightSparkle.svg';

export type ScopeKey = 'scope1' | 'scope2' | 'scope3';

export const SCOPE_SERIES = [
  {key: 'scope1', name: 'Scope 1 (direct)', color: '#024064'},
  {key: 'scope2', name: 'Scope 2 (energy, indirect)', color: '#38B5B0'},
  {key: 'scope3', name: 'Scope 3 (value chain)', color: '#92C980'}
] as const;

export const SCOPE_COLORS: Record<ScopeKey, string> = {
  scope1: SCOPE_SERIES[0].color,
  scope2: SCOPE_SERIES[1].color,
  scope3: SCOPE_SERIES[2].color
};

export const numberFormatter = new Intl.NumberFormat('en-IN');

export const BaselinePill: React.FC<{children?: React.ReactNode}> = ({
  children
}) => (
  <span className="yoyPerformanceBaselinePill">{children ?? 'Baseline'}</span>
);

export const InsightToggleButton: React.FC<{
  open: boolean;
  onToggle: () => void;
}> = ({open, onToggle}) => (
  <Button
    className="yoyPerformanceInsightToggle"
    icon={<AIInsightSparkle aria-hidden="true" />}
    aria-expanded={open}
    onClick={onToggle}
  >
    {open ? 'Hide AI insight' : 'View AI insight'}
  </Button>
);

// A change that rounds to zero is shown as a neutral pill rather than
// "↑ +0.00%". `fractionDigits` caps the displayed precision.
export const ChangePill: React.FC<{
  change: number;
  fractionDigits?: number;
}> = ({change, fractionDigits = 1}) => {
  const rounded = Number(change.toFixed(fractionDigits));
  const formatted = Math.abs(rounded).toFixed(fractionDigits);

  if (rounded === 0) {
    return <span className="yoyPerformanceChange neutral">{formatted}%</span>;
  }

  return (
    <span className={`yoyPerformanceChange ${rounded < 0 ? 'down' : 'up'}`}>
      {rounded < 0 ? `↓ ${formatted}%` : `↑ +${formatted}%`}
    </span>
  );
};

export interface ScopeTooltipRow {
  scope1: number | null;
  scope2: number | null;
  scope3: number | null;
  total: number | null;
  isBaseline?: boolean;
}

const TooltipBody = ({
  label,
  formatLabel,
  formatValue,
  row
}: {
  label?: string;
  formatLabel?: (label: string) => string;
  formatValue: (value: number) => string;
  row: ScopeTooltipRow;
}): React.ReactElement => (
  <>
    <div className="yoyPerformanceTooltipHeader">
      <strong>{formatLabel && label ? formatLabel(label) : label}</strong>
      {row.isBaseline ? <BaselinePill /> : null}
    </div>
    {SCOPE_SERIES.map((series) => (
      <div className="yoyPerformanceTooltipRow" key={series.key}>
        <span>
          <i style={{backgroundColor: series.color}} />
          {series.name.split(' (')[0]}
        </span>
        <strong>{formatValue(Number(row[series.key] ?? 0))} tCO2e</strong>
      </div>
    ))}
    <div className="yoyPerformanceTooltipTotal">
      <span>Total</span>
      <strong>{formatValue(row.total ?? 0)} tCO2e</strong>
    </div>
  </>
);

// Drawn on the page so the chart cannot clip it, then shifted so it stays
// inside the chart column and on screen instead of covering the insight panel.
const FloatingTooltip: React.FC<{
  x?: number;
  y?: number;
  children: React.ReactNode;
}> = ({x, y, children}) => {
  const markerRef = React.useRef<HTMLDivElement>(null);
  const [box, setBox] = React.useState<{top: number; left: number} | null>(
    null
  );

  React.useLayoutEffect(() => {
    const wrapper = markerRef.current?.parentElement;

    if (!wrapper) {
      return undefined;
    }

    const place = (): void => {
      const rect = wrapper.getBoundingClientRect();

      if (rect.width === 0 || rect.height === 0) {
        return;
      }

      const chart = wrapper.closest('.yoyPerformanceChartPanel');
      const chartRect = chart?.getBoundingClientRect();
      const gap = 8;
      const minLeft = Math.max(gap, chartRect ? chartRect.left + gap : gap);
      const minTop = Math.max(gap, chartRect ? chartRect.top + gap : gap);
      const maxRight = Math.min(
        window.innerWidth - gap,
        chartRect ? chartRect.right - gap : window.innerWidth - gap
      );
      const maxBottom = Math.min(
        window.innerHeight - gap,
        chartRect ? chartRect.bottom - gap : window.innerHeight - gap
      );

      let left = rect.left;
      let top = rect.top;

      if (left + rect.width > maxRight) {
        left = maxRight - rect.width;
      }

      if (left < minLeft) {
        left = minLeft;
      }

      if (top + rect.height > maxBottom) {
        top = maxBottom - rect.height;
      }

      if (top < minTop) {
        top = minTop;
      }

      const nextTop = Math.round(top);
      const nextLeft = Math.round(left);

      setBox((current) =>
        current?.top === nextTop && current.left === nextLeft
          ? current
          : {top: nextTop, left: nextLeft}
      );
    };

    place();
    const frame = window.requestAnimationFrame(place);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [x, y, children]);

  return (
    <>
      <div
        ref={markerRef}
        className="yoyPerformanceTooltip emissionsTrendTooltipFloating"
        style={{visibility: 'hidden'}}
        aria-hidden="true"
      >
        {children}
      </div>
      {box
        ? createPortal(
            <div
              className="yoyPerformanceTooltip emissionsTrendTooltipFloating"
              style={{
                position: 'fixed',
                top: box.top,
                left: box.left,
                zIndex: 2000,
                pointerEvents: 'none'
              }}
            >
              {children}
            </div>,
            document.body
          )
        : null}
    </>
  );
};

export const ScopeTooltip = ({
  active,
  payload,
  label,
  formatLabel,
  formatValue = (value: number) => numberFormatter.format(value),
  portal = false,
  coordinate
}: {
  active?: boolean;
  payload?: Array<{payload?: ScopeTooltipRow}>;
  label?: string;
  formatLabel?: (label: string) => string;
  formatValue?: (value: number) => string;
  portal?: boolean;
  coordinate?: {x?: number; y?: number};
}): React.ReactElement | null => {
  if (!active || !payload?.length) {
    return null;
  }

  // Categories kept on the axis but not filled with data have nothing to show.
  const row = payload[0].payload;

  if (row?.total === null || row?.total === undefined) {
    return null;
  }

  // Read the reported figures from the row, not the drawn (possibly raised)
  // segment heights.
  const body = (
    <TooltipBody
      label={label}
      formatLabel={formatLabel}
      formatValue={formatValue}
      row={row}
    />
  );

  if (portal) {
    return (
      <FloatingTooltip x={coordinate?.x} y={coordinate?.y}>
        {body}
      </FloatingTooltip>
    );
  }

  return <div className="yoyPerformanceTooltip">{body}</div>;
};

export const CategoryAxisTick: React.FC<{
  x?: number;
  y?: number;
  payload?: {value: string};
  baselineCategory?: string;
}> = ({x = 0, y = 0, payload, baselineCategory}) => (
  <foreignObject x={x - 60} y={y + 4} width={120} height={44}>
    <div className="yoyPerformanceAxisTick">
      <span>{payload?.value}</span>
      {payload?.value === baselineCategory ? <BaselinePill /> : null}
    </div>
  </foreignObject>
);

export interface ChartScale {
  (value: number | string): number;
  bandwidth?: () => number;
}

// Props recharts injects into a <Customized /> component.
export interface ChartLayoutProps {
  xAxisMap?: Record<string, {scale: ChartScale}>;
  yAxisMap?: Record<string, {scale: ChartScale}>;
  offset?: {left: number; top: number; width: number; height: number};
}

export const getChartScales = ({
  xAxisMap,
  yAxisMap
}: ChartLayoutProps): {
  xScale?: ChartScale;
  yScale?: ChartScale;
  bandwidth: number;
} => {
  const xScale = xAxisMap ? Object.values(xAxisMap)[0]?.scale : undefined;
  const yScale = yAxisMap ? Object.values(yAxisMap)[0]?.scale : undefined;

  return {xScale, yScale, bandwidth: xScale?.bandwidth?.() ?? 0};
};

// Category under the pointer, or null when the hovered category has no data
// (it has no bar or tooltip to highlight).
export const activeCategoryOf = <T extends {total: number | null}>(
  rows: T[],
  index: number | null,
  getCategory: (row: T) => string
): string | null => {
  if (index === null) {
    return null;
  }

  const row = rows[index] as T | undefined;

  if (!row || row.total === null) {
    return null;
  }

  return getCategory(row);
};

// Share of the category band the highlight covers, so it reads as a bar
// highlight rather than a full-width band.
const HIGHLIGHT_BAND_SHARE = 0.68;

// Highlight drawn behind the hovered category's bar. `activeCategory` is null
// when nothing (or a category without data) is hovered.
export const HoverHighlight: React.FC<
  ChartLayoutProps & {activeCategory: string | null; maxWidth: number}
> = ({activeCategory, maxWidth, ...layout}) => {
  const {xScale, bandwidth} = getChartScales(layout);

  if (!xScale || !layout.offset || activeCategory === null) {
    return null;
  }

  const width = Math.min(maxWidth, bandwidth * HIGHLIGHT_BAND_SHARE);

  return (
    <Rectangle
      x={xScale(activeCategory) + (bandwidth - width) / 2}
      y={layout.offset.top}
      width={width}
      height={layout.offset.height}
      fill="#07394A"
      fillOpacity={0.08}
      radius={[20, 20, 0, 0]}
    />
  );
};

export const ScopeLegendItems: React.FC = () => (
  <>
    {SCOPE_SERIES.map((series) => (
      <span key={series.key}>
        <i style={{backgroundColor: series.color}} />
        {series.name}
      </span>
    ))}
  </>
);

export const SCOPE_SPLIT_FOOTNOTE =
  '≈ Monthly scope splits are estimated from bar heights. Scope 3 is reported annually only.';

// Matches `.yoyPerformanceYAxis` so the fixed scale lines up with the plot.
export const Y_AXIS_WIDTH = 48;

const emissionsAxisTick = {
  fill: '#657C81',
  fontSize: 11,
  fontFamily: 'Montserrat'
};

export const formatEmissionsAxisTick = (value: number): string =>
  value === 0 ? '0' : `${value / 1000}k`;

// Y-axis kept outside the horizontal scroller. Invisible series give it the
// same domain as the plot, so the labels stay put and still meet the grid.
export const StickyEmissionsAxis: React.FC<{
  rows: object[];
  xKey: string;
  baseline: number | null;
  includeTotalLine?: boolean;
}> = ({rows, xKey, baseline, includeTotalLine = false}) => (
  <div className="yoyPerformanceYAxis" aria-hidden="true">
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart
        data={rows}
        margin={{top: 72, right: 0, bottom: 0, left: 0}}
      >
        <XAxis
          dataKey={xKey}
          height={48}
          tick={false}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          width={Y_AXIS_WIDTH}
          tick={emissionsAxisTick}
          tickFormatter={formatEmissionsAxisTick}
        />
        <Bar
          dataKey="drawnScope1"
          stackId="emissions"
          fill="transparent"
          stroke="none"
          isAnimationActive={false}
        />
        <Bar
          dataKey="drawnScope2"
          stackId="emissions"
          fill="transparent"
          stroke="none"
          isAnimationActive={false}
        />
        <Bar
          dataKey="drawnScope3"
          stackId="emissions"
          fill="transparent"
          stroke="none"
          isAnimationActive={false}
        />
        {includeTotalLine ? (
          <Line
            dataKey="drawnTotal"
            stroke="transparent"
            dot={false}
            activeDot={false}
            isAnimationActive={false}
          />
        ) : null}
        {baseline !== null ? (
          <ReferenceLine y={baseline} stroke="transparent" />
        ) : null}
      </ComposedChart>
    </ResponsiveContainer>
  </div>
);
